/**
 * Muse 2016 / Muse 2 / original Muse S: one notification per EEG electrode.
 * Wire format cross-checked against urish/muse-js and muse-lsl (see NOTICE.md).
 * Legacy PPG has three channels and is not the Athena four-channel optics signal.
 */
export const LEGACY_EEG_CHARACTERISTICS = [
  "273e0003-4c4d-454d-96be-f03bac821358", // TP9
  "273e0004-4c4d-454d-96be-f03bac821358", // AF7
  "273e0005-4c4d-454d-96be-f03bac821358", // AF8
  "273e0006-4c4d-454d-96be-f03bac821358", // TP10
] as const;
export const LEGACY_TELEMETRY_CHARACTERISTIC = "273e000b-4c4d-454d-96be-f03bac821358";
export const LEGACY_PPG_CHARACTERISTICS = [
  "273e000f-4c4d-454d-96be-f03bac821358", // ambient
  "273e0010-4c4d-454d-96be-f03bac821358", // infrared
  "273e0011-4c4d-454d-96be-f03bac821358", // red
] as const;
export const LEGACY_PRESET = "p21";
/** LibMuse Preset51 enables 64 Hz PPG with this app's four 256 Hz EEG channels. */
export const LEGACY_PPG_PRESET = "p51";
export const LEGACY_SAMPLES_PER_PACKET = 12;
export const LEGACY_PPG_SAMPLES_PER_PACKET = 6;

/** Pause, select the EEG/PPG preset, request status, then start legacy streaming. */
export function legacyStartupSequence(enablePpg = false): [string, number][] {
  return [["h", 0], [enablePpg ? LEGACY_PPG_PRESET : LEGACY_PRESET, 0], ["s", 0], ["d", 0]];
}

export type LegacyEegPacket = { sequence: number; samples: Float32Array };
export type LegacyPpgPacket = { sequence: number; samples: Float32Array };

/** u16 BE packet index, followed by twelve packed unsigned 12-bit BE samples. */
export function decodeLegacyEeg(payload: Uint8Array): LegacyEegPacket | null {
  if (payload.length !== 20) return null;
  const samples = new Float32Array(LEGACY_SAMPLES_PER_PACKET);
  for (let i = 2, sample = 0; i < payload.length; i += 3) {
    samples[sample++] = (((payload[i]! << 4) | (payload[i + 1]! >> 4)) - 2048) * 0.48828125;
    samples[sample++] = ((((payload[i + 1]! & 0x0f) << 8) | payload[i + 2]!) - 2048) * 0.48828125;
  }
  return { sequence: payload[0]! * 256 + payload[1]!, samples };
}

/** u16 BE packet index, followed by six unsigned 24-bit BE intensity samples. */
export function decodeLegacyPpg(payload: Uint8Array): LegacyPpgPacket | null {
  if (payload.length !== 20) return null;
  const samples = new Float32Array(LEGACY_PPG_SAMPLES_PER_PACKET);
  for (let i = 2, sample = 0; i < payload.length; i += 3) {
    samples[sample++] = payload[i]! * 65536 + payload[i + 1]! * 256 + payload[i + 2]!;
  }
  return { sequence: payload[0]! * 256 + payload[1]!, samples };
}

/** Telemetry fields are BE; battery state of charge is u16 at byte 2 / 512. */
export function decodeLegacyBattery(payload: Uint8Array): number | null {
  if (payload.length < 10) return null;
  return Math.min(100, (payload[2]! * 256 + payload[3]!) / 512);
}

type PendingChannels = { channels: (Float32Array | null)[]; arrival: number };
export type LegacyEegBlock = {
  samples: Float32Array[];
  arrival: number;
  lostSamples: number;
  /** Missing electrode notifications, including wholly missing sample groups. */
  lostPackets: number;
};

/**
 * Join channels by their shared packet index, never by notification arrival order.
 * A newer complete group proves an incomplete older group can be flushed with
 * NaNs. Four pending groups also bound delay/memory when one electrode is silent.
 * Old/duplicate notifications are ignored; a u16 wrap continues the same clock.
 */
class LegacyChannelAssembler {
  #pending = new Map<number, PendingChannels>();
  #newest: number | null = null;
  #emitted: number | null = null;

  constructor(readonly channels: number, readonly samplesPerPacket: number) {}

  reset(): void {
    this.#pending.clear();
    this.#newest = this.#emitted = null;
  }

  push(channel: number, packet: LegacyEegPacket, arrival: number): LegacyEegBlock[] {
    if (!Number.isInteger(channel) || channel < 0 || channel >= this.channels || packet.samples.length !== this.samplesPerPacket) return [];
    let index = packet.sequence;
    if (this.#newest !== null) {
      const delta = ((packet.sequence - (this.#newest % 65536) + 32768 + 65536) % 65536) - 32768;
      index = this.#newest + delta;
    }
    if (this.#emitted !== null && index <= this.#emitted) return [];
    this.#newest = Math.max(this.#newest ?? index, index);
    let group = this.#pending.get(index);
    if (!group) {
      group = { channels: Array.from({ length: this.channels }, () => null), arrival };
      this.#pending.set(index, group);
    }
    if (group.channels[channel]) return [];
    group.channels[channel] = packet.samples;
    group.arrival = Math.max(group.arrival, arrival);

    const complete = [...this.#pending].filter(([, value]) => value.channels.every(Boolean)).map(([key]) => key);
    const completeThrough = complete.length ? Math.max(...complete) : Number.NEGATIVE_INFINITY;
    const blocks: LegacyEegBlock[] = [];
    for (const key of [...this.#pending.keys()].sort((a, b) => a - b)) {
      if (key > completeThrough && this.#newest - key < 4) break;
      const next = this.#pending.get(key)!;
      const skipped = this.#emitted === null ? 0 : Math.max(0, key - this.#emitted - 1);
      blocks.push({
        samples: next.channels.map((samples) => samples ?? new Float32Array(this.samplesPerPacket).fill(NaN)),
        arrival: next.arrival,
        lostSamples: skipped * this.samplesPerPacket,
        lostPackets: skipped * this.channels + next.channels.filter((samples) => samples === null).length,
      });
      this.#pending.delete(key);
      this.#emitted = key;
    }
    return blocks;
  }
}

export class LegacyEegAssembler extends LegacyChannelAssembler {
  constructor() { super(4, LEGACY_SAMPLES_PER_PACKET); }
}

/** Independent PPG indices must never share EEG's assembler or sample clock. */
export class LegacyPpgAssembler extends LegacyChannelAssembler {
  constructor() { super(3, LEGACY_PPG_SAMPLES_PER_PACKET); }
}
